import { memo, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconButton, Tooltip, Stack, Icon } from 'investira.react.components';
import { formats, dates, validators } from 'investira.sdk';
import { AssinaturaEstagio } from '../../../components/molecules';
import { BulkContextActions, DataTable } from '../../../components/organisms';
import withEnquadramentos from '../../../hoc/withEnquadramentos';
import { ASSINATURA_ESTAGIO } from '../../../enums';

const EnquadramentoSettings = memo(props => {
  const {
    enquadramentos,
    assinatura,
    state,
    columns,
    renderDetailPanel,
    onBulkDeleteClick,
    ...restProps
  } = props;

  const xEnquadramentos = useMemo(
    () =>
      props.enquadramentos.map(xEnquadramento => {
        const { assinaturas, ...xRest } = xEnquadramento;
        return {
          ...xRest,
          assinatura_estagio: assinaturas?.assinatura_estagio || null,
          registrador_username: assinaturas?.registrador_username || null,
          conferidor_username: assinaturas?.conferidor_username || null,
          verificador_username: assinaturas?.verificador_username || null,
          aprovador_username: assinaturas?.aprovador_username || null
        };
      }),
    [props.enquadramentos]
  );

  const navigate = useNavigate();
  const location = useLocation();

  const xColumns = useMemo(() => {
    return [
      {
        id: 'enquadramento',
        accessorKey: 'enquadramento',
        header: 'Enquadramento',
        enableColumnActions: false,
        enableSorting: false,
        //size: 10000,
        muiTableHeadCellProps: {
          sx: {
            width: '100% !important'
          }
        },
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return null;
          }
          return cell.getValue();
        }
      },
      {
        id: 'vigencia_inicio',
        accessorKey: 'vigencia_inicio',
        header: 'Vigência Início',
        enableColumnActions: false,
        enableSorting: false,
        //size: 10000,
        muiTableHeadCellProps: {
          sx: {
            width: '100% !important'
          }
        },
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return null;
          }
          return formats.formatDate(dates.toDate(cell.getValue()));
        }
      },
      {
        id: 'vigencia_fim',
        accessorKey: 'vigencia_fim',
        header: 'Vigência Fim',
        enableColumnActions: false,
        enableSorting: false,
        //size: 10000,
        muiTableHeadCellProps: {
          sx: {
            width: '100% !important'
          }
        },
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return null;
          }
          return formats.formatDate(dates.toDate(cell.getValue()));
        }
      },
      {
        id: 'assinatura_estagio',
        accessorKey: 'assinatura_estagio',
        header: 'Assinatura',
        enableColumnActions: false,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: assinatura,
        maxSize: 70,
        muiTableBodyCellProps: {
          align: 'center',
          style: { textAlign: 'center' }
        },
        muiTableHeadCellProps: {
          align: 'center',
          style: { textAlign: 'center' }
        },

        Cell: xProps => {
          const { cell, row } = xProps;
          if (!validators.isNull(cell.getValue())) {
            return (
              <AssinaturaEstagio
                value={cell.getValue()}
                variant="icon"
                registradorUsername={row.original.registrador_username}
                conferidorUsername={row.original.conferidor_username}
                verificadorUsername={row.original.verificador_username}
                aprovadorUsername={row.original.aprovador_username}
              />
            );
          }

          return null;
        }
      }
    ];
  }, []);

  const xProps = {
    columns: xColumns,
    data: xEnquadramentos,
    readData: props.readEntidadeEnquadramentos,
    isLoading: props.isLoading,
    initialState: {
      pagination: props.pagination,
      columnVisibility: { carteira: false, assinatura_estagio: assinatura },
      columnSizing: {
        // Define larguras fixas para as colunas de seleção e ações
        'mrt-row-select': 50, // Largura fixa para a coluna de seleção
        'mrt-row-actions': 80 // Largura fixa para a coluna de ações
      }
    },
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    getRowId: row => row.log_id,
    state: {
      isLoading: props.isLoading,
      pagination: props.pagination,
      showProgressBars: props.isRefetching,
      columnFilters: props.columnFilters,
      sorting: props.sorting,
      rowSelection: props.rowSelection
    },
    rowCount: props.rowCount,
    onColumnFiltersChange: props.setColumnFilters,
    onSortingChange: props.setSorting,
    onPaginationChange: props.setPagination,
    onRowSelectionChange: props.setRowSelection,
    onGlobalFilterChange: props.setGlobalFilter,
    //onBulkDeleteClick: props.bulkDeleteLogs,
    enableRowSelection: true,
    renderTopToolbarCustomActions: ({ table }) => {
      const selectedRows = table.getSelectedRowModel().rows;
      const isSomeRowSelected = selectedRows.length > 0;
      const isInvalidRows = selectedRows.every(row => row.original.assinatura_estagio === null);

      return (
        <BulkContextActions
          onDeleteClick={() => {
            props.bulkDeleteEnquadramentos(selectedRows);
          }}
          disabled={props.isLoading || props.isRefetching || !isSomeRowSelected}
          assinatura={assinatura}
          recurso="CFENQ"
          selectedRows={selectedRows}
          isInvalidRows={isInvalidRows}
          refreshData={props.readEntidadeEnquadramentos}
        />
      );
    },
    muiTableContainerProps: {
      sx: {
        maxWidth: '100%' // Garante que a tabela ocupe 100% do espaço disponível
      }
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 80 // largura em pixels para a coluna de ações
      },
      'mrt-row-select': {
        size: 50 // largura em pixels para a coluna de seleção
      }
    },
    renderRowActions: ({ row }) => {
      const xAssinaturaEstagio = row.original.assinatura_estagio;
      const isDisabled =
        xAssinaturaEstagio !== null && xAssinaturaEstagio !== ASSINATURA_ESTAGIO.RG.id;
      return (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Visualizar">
            <span>
              <IconButton
                color="primary"
                onClick={() =>
                  navigate(`${location.pathname}/${row.original.enquadramento_id}`, {
                    state: row.original
                  })
                }>
                <Icon iconName="eye" size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Editar">
            <span>
              <IconButton
                color="primary"
                onClick={() =>
                  navigate(`${location.pathname}/edit`, { state: { ...row.original, assinatura } })
                }
                disabled={isDisabled}>
                <Icon iconName="edit" size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      );
    },
    ...restProps
  };

  return <DataTable {...xProps} />;
});

EnquadramentoSettings.displayName = 'EnquadramentoSettings';

export default withEnquadramentos(EnquadramentoSettings);
